// ✅ src/pages/VehicleV2/DashboardReport_Print.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "https://nissi-india.com/api/dashboardReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const normStatus = (v) => {
  const s = toStr(v);
  if (!s) return "";
  // keep as-is, but normalize common values
  const low = s.toLowerCase();
  if (low === "1" || low === "y" || low === "yes" || low === "true") return "Yes";
  if (low === "0" || low === "n" || low === "no" || low === "false") return "No";
  return s;
};

export default function DashboardReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ received from list page (your handlePrint)
  const passed = location?.state || {};

  // your passed state keys:
  // { branchType, branchidalt, store, veh_code, veh_name, veh_no }
  const filters = useMemo(() => {
    return {
      branchtype: toStr(passed?.branchType),
      branchid: Number(passed?.branchidalt) || 0,
      store: toStr(passed?.store || "%"),
      veh_code: toStr(passed?.veh_code || "%"),
      veh_name: toStr(passed?.veh_name || "%"),
      veh_no: toStr(passed?.veh_no || "%"),
      userid: Number(sessionStorage.getItem("loginuserid")) || 0,
    };
  }, [passed]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      // ✅ if user opened print page directly without state
      if (!passed || (!passed.branchidalt && passed.branchidalt !== 0)) {
        throw new Error("Print params missing. Open print from Dashboard Report page.");
      }

      const { data } = await axios.get(`${API_BASE}/print`, { params: filters });
      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- column mapping (match screenshot labels) ---------------- */
  const getVehicleMainType = (r) =>
    pick(r, ["VehicleMainType", "vehicleMainType", "fld_vehiclemaintype", "fld_vehiclemaintypename", "vehicle_main_type"], "-");

  const getEquipmentName = (r) =>
    pick(r, ["fld_vehicletypename", "equipmentName", "fld_equipmentname", "fld_vehiclename", "veh_name", "VehicleName"], "-");

  const getEquipmentReg = (r) =>
    pick(r, ["fld_vehicle_regno", "equipmentReg", "fld_equipmentreg", "fld_vehicleregno", "veh_no", "VehicleNo", "RegNo"], "-");

  const getInsurance = (r) =>
    normStatus(pick(r, ["fld_insurancerenewaldate", "insurance", "fld_insurance", "InsuranceStatus", "insurance_status"], ""));

  const getFC = (r) =>
    normStatus(pick(r, ["fld_fcrenewaldate", "FC", "fc", "fld_fc", "FCStatus", "fc_status"], ""));

  const getRoadTax = (r) =>
    normStatus(pick(r, ["fld_roadtaxto_date", "roadTax", "fld_roadtax", "RoadTaxStatus", "road_tax_status"], ""));

  const getPermit = (r) =>
    normStatus(pick(r, ["fld_pemitexpire_date", "permit", "fld_permit", "PermitStatus", "permit_status"], ""));

  const getFreeService = (r) =>
    normStatus(pick(r, ["fld_freeservice_date", "freeService", "fld_freeservice", "FreeServiceStatus", "free_service_status"], ""));

  const getStoreName = (r) =>
    pick(r, ["StoreName", "storeName", "fld_storename", "Store", "store"], "-");

  return (
    
      <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
        {/* HeaderBar only for screen */}
       

        <div className="w-full bg-white">
          {/* ✅ PRINT CSS */}
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Toolbar */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? (
                "Loading..."
              ) : errMsg ? (
                <span className="text-red-600">{errMsg}</span>
              ) : (
                `Records: ${rows.length}`
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>
              <button
                onClick={() => window.print()}
                disabled={loading || !rows.length}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          {/* ✅ PRINT TEMPLATE (same style as your Battery print) */}
          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  Expired Equipment List
                </div>

                {/* Filter info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Branch Type</span> : {filters.branchtype || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Branch</span> : {filters.branchid || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Store</span> : {filters.store || "-"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div>
                        <span className="font-semibold">Vehicle Code</span> : {filters.veh_code || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vehicle Name</span> : {filters.veh_name || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vehicle No</span> : {filters.veh_no || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table (match screenshot columns + borders) */}
                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-2 py-1 w-[6%]">S.No</th>
                        <th className="border border-black px-2 py-1 w-[18%]">Vehicle Main type</th>
                        <th className="border border-black px-2 py-1 w-[14%]">Equipment Name</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Equipment Reg</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Insurance</th>
                        <th className="border border-black px-2 py-1 w-[8%]">Fc</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Road Tax</th>
                        <th className="border border-black px-2 py-1 w-[8%]">Permit</th>
                        <th className="border border-black px-2 py-1 w-[8%]">Free Service</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Store Name</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!loading && rows.length === 0 ? (
                        <tr>
                          <td className="border border-black px-2 py-2 text-center" colSpan={10}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={pick(r, ["id", "fld_id", "RowId"], `${idx}`)}>
                            <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border border-black px-2 py-1">{getVehicleMainType(r)}</td>
                            <td className="border border-black px-2 py-1">{getEquipmentName(r)}</td>
                            <td className="border border-black px-2 py-1">{getEquipmentReg(r)}</td>
                            <td className="border border-black px-2 py-1 text-center">{getInsurance(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-center">{getFC(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-center">{getRoadTax(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-center">{getPermit(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-center">{getFreeService(r) || "-"}</td>
                            <td className="border border-black px-2 py-1">{getStoreName(r)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug (optional) */}
            {rows?.length > 0 && (
              <div className="no-print mt-4 text-xs text-gray-600">
                <details>
                  <summary className="cursor-pointer">Debug: View passed state + rows count</summary>
                  <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                    {JSON.stringify({ passed, fetchedRowsCount: rows.length }, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
   
  );
}
